import React, { useCallback, useEffect, useState } from "react";
import { apiRequest } from "../../helpers/api";
import { moduleForPath, useAdminAccess } from "../AdminAccessContext";
import {
  DashboardHeader,
  DashboardSkeleton,
  EmptyState,
  InsightList,
  Panel,
  PanelLink,
  ProportionList,
  QuickActions,
  RefreshButton,
  StatCard,
  TrendChart,
  formatCount,
} from "../../components/dashboard/DashboardKit";
import "./AdminDashboard.css";

const formatDate = (value) => value ? new Date(value).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

const statCards = [
  ["totalUsers", "Customer accounts", "ti-users", "teal", "Across the platform", "/admin/users"],
  ["newUsersLast30Days", "New this month", "ti-user-plus", "green", "Last 30 days", "/admin/users"],
  ["activeSubscriptions", "Active subscriptions", "ti-credit-card", "blue", "Currently active", "/admin/subscriptions"],
  ["aiGenerationsLast30Days", "AI generations", "ti-sparkles", "amber", "Last 30 days", "/admin/ai-providers"],
  ["connectedSocialAccounts", "Connected channels", "ti-plug-connected", "rose", "Currently connected", "/admin/integrations"],
  ["openSupportTickets", "Open support", "ti-lifebuoy", "coral", "Awaiting resolution", "/admin/support-tickets"],
];

const quickActionCatalog = [
  { to: "/admin/users", icon: "ti-users", label: "Browse customers", description: "Accounts, status and usage", tone: "teal" },
  { to: "/admin/users?businessStatus=complete", icon: "ti-building-store", label: "Browse businesses", description: "Customers with a business profile", tone: "blue" },
  { to: "/admin/support-tickets", icon: "ti-lifebuoy", label: "Review support", description: "Respond to open tickets", tone: "coral" },
  { to: "/admin/subscriptions", icon: "ti-ticket", label: "Plans & coupons", description: "Pricing and promotions", tone: "amber" },
  { to: "/admin/integrations", icon: "ti-plug-connected", label: "Configure social OAuth", description: "Channel app credentials", tone: "rose" },
  { to: "/admin/ai-providers", icon: "ti-sparkles", label: "AI providers", description: "Models and API keys", tone: "green" },
];

const activityMeta = {
  user: { label: "New customer", icon: "ti-user-plus", tone: "teal", path: "/admin/users" },
  post: { label: "Content created", icon: "ti-notes", tone: "blue", path: "/admin/users" },
  support: { label: "Support request", icon: "ti-lifebuoy", tone: "coral", path: "/admin/support-tickets" },
  subscription: { label: "Subscription update", icon: "ti-credit-card", tone: "amber", path: "/admin/subscriptions" },
};

const fallbackActivity = { label: "Platform activity", icon: "ti-activity", tone: "teal" };
const sumTrend = (items = []) => items.reduce((total, item) => total + (Number(item.count) || 0), 0);

const buildInsights = (stats, trends, canView) => {
  const insights = [];
  if (stats.openSupportTickets > 0) insights.push({ id: "support", tone: stats.openSupportTickets > 10 ? "critical" : "warning", icon: "ti-lifebuoy", title: `${formatCount(stats.openSupportTickets)} support ticket${stats.openSupportTickets === 1 ? "" : "s"} awaiting resolution`, text: "Open, in progress or waiting for the customer.", to: "/admin/support-tickets", cta: "Review tickets" });
  else insights.push({ id: "support-clear", tone: "good", icon: "ti-circle-check", title: "Support queue is clear", text: "There are no open tickets right now." });
  const newUsers = sumTrend(trends.users);
  if (newUsers > 0) insights.push({ id: "signups", tone: "good", icon: "ti-user-plus", title: `${formatCount(newUsers)} new customer${newUsers === 1 ? "" : "s"} in 14 days`, text: `${formatCount(stats.newUsersLast30Days)} joined over the last 30 days.`, to: "/admin/users", cta: "View customers" });
  else insights.push({ id: "no-signups", tone: "warning", icon: "ti-user-off", title: "No new sign-ups in 14 days", text: "Registrations have been quiet for two weeks." });
  if (stats.totalUsers > 0) {
    const coverage = Math.round((stats.activeSubscriptions / stats.totalUsers) * 100);
    insights.push({ id: "conversion", tone: "info", icon: "ti-chart-pie", title: `${coverage}% of customers hold an active subscription`, text: `${formatCount(stats.activeSubscriptions)} active out of ${formatCount(stats.totalUsers)} accounts.`, to: "/admin/subscriptions", cta: "Manage plans" });
  }
  return insights.filter((insight) => !insight.to || canView(insight.to));
};

const AdminDashboard = () => {
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const { canViewModule } = useAdminAccess();
  const canView = (path) => canViewModule(moduleForPath(path.split("?")[0]));

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setOverview(await apiRequest("/api/admin/dashboard"));
    } catch (requestError) {
      setError(requestError.message || "Unable to load the platform overview.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  const stats = overview?.stats || {};
  const trends = overview?.trends || {};
  const today = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  const month = new Date().toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const quickActions = quickActionCatalog.filter((action) => canView(action.to));
  const insights = overview ? buildInsights(stats, trends, canView) : [];

  return (
    <section className="workspace-dashboard admin-dashboard sx-dashboard">
      <DashboardHeader
        eyebrow={today}
        title="Platform overview"
        intro="A live view of customer growth, content activity and service operations across Socialyorix."
        status={error ? "Data unavailable" : loading ? "Updating overview" : month}
        statusTone={error ? "error" : loading ? "busy" : "live"}
        actions={overview && <RefreshButton onClick={loadOverview} busy={loading} />}
      />

      {error && <div className="sx-alert" role="alert"><span><i className="ti ti-alert-triangle" aria-hidden="true" />{error}</span><button type="button" onClick={loadOverview} disabled={loading}>Retry</button></div>}
      {loading && !overview ? <DashboardSkeleton label="Loading platform data..." /> : overview && <>
        <div className="sx-stats">
          {statCards.map(([key, label, icon, tone, detail, path], position) => (
            <StatCard key={key} index={position + 1} icon={icon} label={label} value={stats[key]} detail={detail} tone={tone} to={canView(path) ? path : undefined} />
          ))}
        </div>

        <div className="sx-grid-3">
          <TrendChart index={7} title="New customer accounts" subtitle="Daily registrations · last 14 days" values={trends.users || []} tone="teal" />
          <TrendChart index={8} title="Content created" subtitle="Daily post activity · last 14 days" values={trends.posts || []} tone="blue" />
          <TrendChart index={9} title="AI usage" subtitle="Daily generation units · last 14 days" values={trends.aiUsage || []} tone="amber" />
        </div>

        <div className="sx-grid-main">
          <Panel index={10} icon="ti-activity" title="Recent activity" subtitle="Latest platform events" count={overview.recentActivity?.length || 0}>
            {overview.recentActivity?.length ? <ul className="sx-feed">{overview.recentActivity.map((activity) => {
              const meta = activityMeta[activity.type] || fallbackActivity;
              return <li key={`${activity.type}-${activity.id}`}>
                <span className={`sx-feed-icon tone-${meta.tone}`} aria-hidden="true"><i className={`ti ${meta.icon}`} /></span>
                <span className="sx-item-copy"><strong>{activity.title}</strong><small>{meta.label}{activity.detail ? ` · ${activity.detail}` : ""}</small></span>
                <time dateTime={activity.createdAt}>{formatDate(activity.createdAt)}</time>
              </li>;
            })}</ul> : <EmptyState icon="ti-activity" title="No recent activity to show." />}
          </Panel>

          <Panel index={11} icon="ti-bulb" title="Operational insights" subtitle="Derived from current platform data">
            <InsightList insights={insights} />
          </Panel>
        </div>

        <div className="sx-grid-2">
          {quickActions.length > 0 && <Panel index={12} icon="ti-bolt" title="Quick actions" subtitle="Jump to a frequently used administration area.">
            <QuickActions actions={quickActions} />
          </Panel>}

          <Panel index={13} icon="ti-credit-card" title="Subscription mix" subtitle="Current plans and account status" action={canView("/admin/subscriptions") && <PanelLink to="/admin/subscriptions">Plans</PanelLink>}>
            {overview.subscriptionPlans?.length
              ? <ProportionList items={overview.subscriptionPlans.map((plan) => ({
                key: plan.name,
                label: plan.name,
                meta: `${formatCount(plan.activeSubscriptions)} active`,
                value: plan.subscriptions,
                tone: "teal",
              }))} />
              : <EmptyState icon="ti-credit-card-off" title="No subscription plans are configured." />}
            <div className={`sx-service${error ? " is-error" : ""}`}>
              <span className="sx-status-dot" aria-hidden="true" />
              <span><strong>Platform data services</strong><small>{overview.generatedAt ? `Last checked ${formatDate(overview.generatedAt)}` : "Connection confirmed"}</small></span>
              <span className="sx-service-label">{error ? "Unavailable" : "Operational"}</span>
            </div>
          </Panel>
        </div>
      </>}
    </section>
  );
};

export default AdminDashboard;
